import { Id } from '@/api/utils/sql';

export enum EventCategory {
  LEASE = 'lease',
  PAYMENT = 'payment',
  MAINTENANCE = 'maintenance',
  INSPECTION = 'inspection',
  OTHER = 'other',
}

export enum ReminderType {
  ONE_TIME = 'one-time',
  RECURRING = 'recurring',
}

export enum RecurringPattern {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  CUSTOM = 'custom',
}

export enum RecurringPeriod {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
  YEARS = 'years',
}

export enum ReminderUnit {
  DAYS_BEFORE = 'days_before',
  WEEKS_BEFORE = 'weeks_before',
  MONTHS_BEFORE = 'months_before',
}

export interface ReminderInterval {
  value: number;
  unit: ReminderUnit;
}

export interface CalendarEventReminder {
  id: number;
  calendar_event_id: number;
  remind_at: string; // ISO date string
  value: number;
  unit: ReminderUnit;
  created_at: string;
  updated_at: string;
}

export interface RecurringCalendarEvent {
  id: number;
  calendar_event_id: number;
  pattern: RecurringPattern;
  every: number;
  period: RecurringPeriod;
  end_date?: string; // ISO date string, null if never expires
  created_at: string;
  updated_at: string;
}

export interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  start: string; // ISO date string
  end: string; // ISO date string
  all_day: boolean;
  propertyLocationId: number;
  buildingId?: number;
  unitId?: number;
  tenantId?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    category?: EventCategory;
    notifySelf?: boolean;
    notifyTenant?: boolean;
    notifyVendor?: boolean;
    emailNotification?: boolean;
    smsNotification?: boolean;
    pushNotification?: boolean;
    dashboardNotification?: boolean;
    vendor?: {
      id?: string;
      email?: string;
      message?: string;
    };
    location?: string;
  };
  reminders?: CalendarEventReminder[];
  recurringSettings?: RecurringCalendarEvent;
  location?: {
    id: Id;
    name: string;
    type: string;
  };
  building?: {
    id: Id;
    name: string;
  };
  unit?: {
    id: Id;
    name: string;
  };
  tenant?: {
    id: Id;
    full_name: string;
  };
}

export interface CreateCalendarEventDto {
  title: string;
  description?: string;
  start: string;
  end: string;
  all_day: boolean;
  property_location_id: number;
  building_id?: number;
  unit_id?: number;
  tenant_id?: number;

  // Additional fields
  category?: EventCategory;
  reminderType?: ReminderType;
  recurringPattern?: RecurringPattern;
  recurringEvery?: number;
  recurringPeriod?: RecurringPeriod;
  neverExpires?: boolean;
  multipleReminders?: boolean;
  reminderIntervals?: ReminderInterval[];

  // List of reminder IDs to delete when updating an event
  deletedReminderIds?: Array<number | string>;

  // Notification settings
  notifySelf?: boolean;
  notifyTenant?: boolean;
  notifyVendor?: boolean;
  vendorId?: string;
  vendorEmail?: string;
  vendorMessage?: string;
  emailNotification?: boolean;
  smsNotification?: boolean;
  pushNotification?: boolean;
  dashboardNotification?: boolean;
  
  // Metadata object for additional fields
  metadata?: Record<string, unknown>;
}

export type ApiResponse_Create_CalendarEvent = CalendarEvent;
export type ApiRequest_Create_CalendarEvent = CreateCalendarEventDto;

export type CalendarEventModel = CalendarEvent;
